/**
 * Utility functions for formatting data in the application
 */

// Date formatting options interface
interface DateFormatOptions extends Intl.DateTimeFormatOptions {
  dateStyle?: 'full' | 'long' | 'medium' | 'short';
  timeStyle?: 'full' | 'long' | 'medium' | 'short';
}

// Number formatting options interface
interface NumberFormatOptions extends Intl.NumberFormatOptions {
  notation?: 'standard' | 'scientific' | 'engineering' | 'compact';
  compactDisplay?: 'short' | 'long';
}

/**
 * Format a number as currency
 */
export function formatCurrency(
  amount: number, 
  currency: string = 'EUR', 
  locale: string = 'fr-FR'
): string {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '-';
  }
  
  const formattedNumber = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
  
  return `${formattedNumber} ${currency}`;
}

/**
 * Format a date
 */
export function formatDate(
  date: string | Date | number, 
  locale: string = 'fr-FR', 
  options: DateFormatOptions = {}
): string {
  if (!date) return '-';
  
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) {
    return '-';
  }
  
  const defaultOptions: DateFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...options
  };
  
  return new Intl.DateTimeFormat(locale, defaultOptions).format(dateObj);
}

/**
 * Format a date and time
 */
export function formatDateTime(
  date: string | Date | number, 
  locale: string = 'fr-FR', 
  options: DateFormatOptions = {}
): string {
  if (!date) return '-';
  
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) {
    return '-';
  }
  
  const defaultOptions: DateFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    ...options
  };
  
  return new Intl.DateTimeFormat(locale, defaultOptions).format(dateObj);
}

/**
 * Format a number with thousand separators
 */
export function formatNumber(
  number: number, 
  locale: string = 'fr-FR', 
  options: NumberFormatOptions = {}
): string {
  if (number === null || number === undefined || isNaN(number)) {
    return '-';
  }
  
  return new Intl.NumberFormat(locale, options).format(number);
}

/**
 * Format a percentage
 */
export function formatPercentage(
  value: number, 
  locale: string = 'fr-FR', 
  decimals: number = 1
): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '-';
  }
  
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value / 100);
}

/**
 * Format file size in human readable format
 */
export function formatFileSize(bytes: number, locale: string = 'fr-FR'): string {
  if (bytes === 0) return '0 B';
  if (bytes < 0 || isNaN(bytes)) return '-';
  
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = bytes / Math.pow(1024, i);
  
  return `${new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: i === 0 ? 0 : 1
  }).format(size)} ${sizes[i]}`;
}

/**
 * Format a phone number
 */
export function formatPhoneNumber(
  phoneNumber: string, 
  country: string = 'FR'
): string {
  if (!phoneNumber) return '-';
  
  // Remove all non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, '');
  
  if (country === 'FR') {
    // French phone number formatting
    if (cleaned.length === 10) {
      return cleaned.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5');
    }
    if (cleaned.length === 9) {
      return cleaned.replace(/(\d{1})(\d{2})(\d{2})(\d{2})(\d{2})/, '0$1 $2 $3 $4 $5');
    }
  }
  
  // Default formatting for other countries or invalid formats
  return phoneNumber;
}

/**
 * Format a postal code
 */
export function formatPostalCode(
  postalCode: string, 
  country: string = 'FR'
): string {
  if (!postalCode) return '-';
  
  if (country === 'FR') {
    // French postal code formatting (5 digits)
    const cleaned = postalCode.replace(/\D/g, '');
    if (cleaned.length === 5) {
      return cleaned;
    }
  }
  
  return postalCode;
}

/**
 * Truncate text with ellipsis
 */
export function truncateText(
  text: string, 
  maxLength: number, 
  ellipsis: string = '...'
): string {
  if (!text || text.length <= maxLength) {
    return text || '';
  }
  
  return text.substring(0, maxLength - ellipsis.length) + ellipsis;
}

/**
 * Format a relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(
  date: string | Date | number, 
  locale: string = 'fr-FR'
): string {
  if (!date) return '-';
  
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) {
    return '-';
  }
  
  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  
  if (diffDays > 0) {
    return rtf.format(-diffDays, 'day');
  } else if (diffHours > 0) {
    return rtf.format(-diffHours, 'hour');
  } else if (diffMinutes > 0) {
    return rtf.format(-diffMinutes, 'minute');
  } else {
    return rtf.format(-diffSeconds, 'second');
  }
}

/**
 * Format a list of items with proper conjunctions
 */
export function formatList(
  items: string[], 
  locale: string = 'fr-FR', 
  style: 'long' | 'short' | 'narrow' = 'long'
): string {
  if (!items || items.length === 0) {
    return '';
  }
  
  if (items.length === 1) {
    return items[0];
  }
  
  const formatter = new Intl.ListFormat(locale, { 
    style, 
    type: 'conjunction' 
  });
  
  return formatter.format(items);
}

/**
 * Capitalize first letter of each word
 */
export function toTitleCase(text: string): string {
  if (!text) return '';
  
  return text.replace(/\w\S*/g, (txt) => 
    txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  );
}

/**
 * Format a name (first and last name)
 */
export function formatName(
  firstName: string, 
  lastName: string, 
  format: 'first-last' | 'last-first' | 'initials' = 'first-last'
): string {
  if (!firstName && !lastName) return '-';
  
  const first = firstName?.trim() || '';
  const last = lastName?.trim() || '';
  
  switch (format) {
    case 'last-first':
      return [last, first].filter(Boolean).join(', ');
    case 'initials':
      const firstInitial = first.charAt(0).toUpperCase();
      const lastInitial = last.charAt(0).toUpperCase();
      return [firstInitial, lastInitial].filter(Boolean).join('');
    case 'first-last':
    default:
      return [first, last].filter(Boolean).join(' ');
  }
}

export type { DateFormatOptions, NumberFormatOptions };
